import { authedFetch } from '@/providers/loginWithEmail/dataProvider';
import { ContractorOwner, QuoteJobFolder } from '@/types';

function parseError(data: unknown, fallback: string): string {
    if (!data || typeof data !== 'object') return fallback;
    const obj = data as Record<string, unknown>;

    const errors = obj.errors;
    if (errors && typeof errors === 'object') {
        const errorsObj = errors as Record<string, unknown>;
        const root = errorsObj.root;
        if (typeof root === 'string') return root;
        if (root && typeof root === 'object' && root !== null && 'serverError' in root) {
            return String((root as { serverError: unknown }).serverError);
        }
        for (const value of Object.values(errorsObj)) {
            if (typeof value === 'string') return value;
        }
    }

    const detail = obj.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
        const first = detail[0];
        if (first && typeof first === 'object' && first !== null && 'msg' in first) {
            return String((first as { msg: unknown }).msg);
        }
    }
    if (detail && typeof detail === 'object') {
        const root = (detail as Record<string, unknown>).root;
        if (typeof root === 'string') return root;
        if (root && typeof root === 'object' && root !== null && 'serverError' in root) {
            return String((root as { serverError: unknown }).serverError);
        }
    }
    return fallback;
}

async function authFetch(path: string, init?: RequestInit) {
    const res = await authedFetch(path, {
        ...init,
        headers: {
            'Content-Type': 'application/json',
            ...(init?.headers || {}),
        },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
        throw new Error(parseError(data, 'Request failed'));
    }
    return data;
}

export type ContractorOwnerCreatePayload = {
    name: string;
    company_name?: string;
    email?: string;
    phone_number?: string;
};

export function contractorOwnerOptionLabel(person: ContractorOwner): string {
    const email = (person.email || '').trim();
    return email ? `${person.name} <${email}>` : person.name;
}

export async function searchContractorOwners(q: string): Promise<ContractorOwner[]> {
    const params = new URLSearchParams();
    if (q.trim()) {
        params.set('filter', JSON.stringify({ q: q.trim() }));
    }
    params.set('range', JSON.stringify([0, 24]));
    params.set('sort', JSON.stringify(['name', 'ASC']));
    const data = await authFetch(`/contractor_owners?${params.toString()}`);
    return data as ContractorOwner[];
}

/** Exact case-insensitive email match. */
export function matchContractorOwnerByEmail(
    owners: ContractorOwner[],
    email: string,
): ContractorOwner | null {
    const needle = email.trim().toLowerCase();
    if (!needle) return null;
    return (
        owners.find(o => (o.email || '').trim().toLowerCase() === needle) ?? null
    );
}

/** Exact case-insensitive name match (same idea as estimator backend name lookup). */
export function matchContractorOwnerByName(
    owners: ContractorOwner[],
    name: string,
): ContractorOwner | null {
    const needle = name.trim().toLowerCase();
    if (!needle) return null;
    return (
        owners.find(o => (o.name || '').trim().toLowerCase() === needle) ?? null
    );
}

/** Find existing contractors_owner by name, or create one. */
export async function findOrCreateContractorOwnerByName(
    name: string,
): Promise<ContractorOwner> {
    const trimmed = name.trim();
    if (!trimmed) throw new Error('Name is required');
    const matches = await searchContractorOwners(trimmed);
    const existing = matchContractorOwnerByName(matches, trimmed);
    if (existing) return existing;
    return createContractorOwner({ name: trimmed });
}

export async function getContractorOwner(id: number): Promise<ContractorOwner> {
    const data = await authFetch(`/contractor_owners/${id}`);
    return data as ContractorOwner;
}

export async function createContractorOwner(
    payload: ContractorOwnerCreatePayload,
): Promise<ContractorOwner> {
    const data = await authFetch(`/contractor_owners`, {
        method: 'POST',
        body: JSON.stringify({
            name: payload.name,
            company_name: payload.company_name ?? '',
            email: payload.email || null,
            phone_number: payload.phone_number || null,
        }),
    });
    return data as ContractorOwner;
}

export async function updateContractorOwner(
    id: number,
    payload: ContractorOwnerCreatePayload,
): Promise<ContractorOwner> {
    const data = await authFetch(`/contractor_owners/${id}`, {
        method: 'PUT',
        body: JSON.stringify({
            name: payload.name,
            company_name: payload.company_name ?? '',
            email: payload.email || null,
            phone_number: payload.phone_number || null,
        }),
    });
    return data as ContractorOwner;
}

/** Update project contractor / property owner contacts; returns updated project payload. */
export async function updateProjectContacts(
    projectId: number,
    contacts: { contractor_id?: number | null; property_owner_id?: number | null },
): Promise<QuoteJobFolder> {
    const body: Record<string, number> = {};
    if (contacts.contractor_id != null) {
        body.contractor_id = contacts.contractor_id;
    }
    if (contacts.property_owner_id != null) {
        body.property_owner_id = contacts.property_owner_id;
    }
    const data = await authFetch(`/projects/${projectId}`, {
        method: 'PUT',
        body: JSON.stringify(body),
    });
    return data as QuoteJobFolder;
}

export type ContractorRankingEntry = {
    id: number;
    name: string;
    count: number;
};

export type ContractorNetworkRankings = {
    by_properties: ContractorRankingEntry[];
    by_owners: ContractorRankingEntry[];
};

export type ContractorNetworkProperty = {
    project_id: number;
    address: string;
    property_owner_id: number | null;
    property_owner_name: string | null;
};

export type ContractorNetworkOwner = {
    id: number;
    name: string;
};

export type ContractorNetwork = {
    properties: ContractorNetworkProperty[];
    owners: ContractorNetworkOwner[];
};

export async function fetchContractorNetworkRankings(): Promise<ContractorNetworkRankings> {
    const data = await authFetch(`/contractor_owners/network-rankings`);
    return data as ContractorNetworkRankings;
}

export async function fetchContractorNetwork(
    id: number,
): Promise<ContractorNetwork> {
    const data = await authFetch(`/contractor_owners/${id}/network`);
    return data as ContractorNetwork;
}
